import { z } from "zod";
import { paginationSchema } from "../interfaces/pagination";
import { paginationMetaSchema } from "../interfaces/base-api-response";
import { locationCoordinatesSchema } from "../interfaces/location";

export enum RecipientType {
  ME = "ME",
  OTHER = "OTHER",
}

export enum AddressType {
  HOME = "HOME",
  WORK = "WORK",
  OTHER = "OTHER",
}

export const addressTypeSchema = z.object({
  code: z.nativeEnum(AddressType),
  name: z.string().nullish(),
});

export const googlePlusCodeSchema = z.object({
  globalCode: z.string().optional(),
  compoundCode: z.string().optional(),
});

/** Recipient as stored and returned. No OTHER rule, so one bad stored row can't fail a whole page. */
export const recipientResponseSchema = z.object({
  code: z.nativeEnum(RecipientType),
  name: z.string().nullish(),
  contact: z.string().nullish(),
});

/** Recipient on create/update: OTHER needs a non-blank name and contact. */
export const recipientSchema = recipientResponseSchema.superRefine((data, ctx) => {
  if (data.code !== RecipientType.OTHER) {
    return;
  }
  if (!data.name?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["name"],
      message: "Recipient name is required when recipient type is OTHER",
    });
  }
  if (!data.contact?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["contact"],
      message: "Recipient contact is required when recipient type is OTHER",
    });
  }
});

export const createUserAddressRequestSchema = z.object({
  coordinates: locationCoordinatesSchema,
  mongoId: z.string().nullish(),
  country: z.string(),
  state: z.string(),
  city: z.string(),
  postalCode: z.string().regex(/^\d{6}$/, "Postal code should be a 6-digit number"),
  area: z.string().optional(),
  type: addressTypeSchema,
  houseNumber: z.string(),
  society: z.string(),
  recipient: recipientSchema.optional(),
  floor: z.string().nullish(),
  landmark: z.string().nullish(),
  formattedAddress: z.string().optional(),
  googlePlaceId: z.string().optional(),
  googlePlusCode: googlePlusCodeSchema.optional(),
  isDefault: z.boolean().optional().default(false),
});

export type CreateV4UserAddressRequest = z.output<typeof createUserAddressRequestSchema>;

/** PUT body matches create for platform v4 address update. */
export const updateUserAddressRequestSchema = createUserAddressRequestSchema;
export type UpdateV4UserAddressRequest = z.output<typeof updateUserAddressRequestSchema>;

export const userAddressResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  coordinates: locationCoordinatesSchema,
  country: z.string(),
  state: z.string(),
  city: z.string(),
  postalCode: z.string(),
  area: z.string().nullish(),
  type: addressTypeSchema,
  houseNumber: z.string(),
  society: z.string(),
  recipient: recipientResponseSchema,
  floor: z.string().nullish(),
  landmark: z.string().nullish(),
  formattedAddress: z.string(),
  googlePlaceId: z.string().nullish(),
  googlePlusCode: googlePlusCodeSchema.nullish(),
  createdAt: z.number(),
  updatedAt: z.number(),
  isDefault: z.boolean(),
});

export type UserAddressResponse = z.output<typeof userAddressResponseSchema>;

export const listUserAddressesQuerySchema = paginationSchema;
export type ListUserAddressesQuery = z.output<typeof listUserAddressesQuerySchema>;

export const userAddressListResponseSchema = z.object({
  items: z.array(userAddressResponseSchema),
  meta: paginationMetaSchema,
});

export type UserAddressListResponse = z.output<typeof userAddressListResponseSchema>;
